import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, HeartHandshake, ClipboardCheck, Users, Award, Target, Eye, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
const VALUES = [{
  letter: 'I',
  title: 'Integrity',
  description: 'We act honestly and ethically in every interaction, earning the trust of those we serve.',
  Icon: ShieldCheck
}, {
  letter: 'C',
  title: 'Commitment',
  description: 'We are dedicated to the health and well-being of every patient who walks through our doors.',
  Icon: HeartHandshake
}, {
  letter: 'A',
  title: 'Accountability',
  description: 'We take responsibility for our actions and the quality of care we deliver.',
  Icon: ClipboardCheck
}, {
  letter: 'R',
  title: 'Respect',
  description: 'We treat patients, families, and colleagues with dignity, compassion, and kindness.',
  Icon: Users
}, {
  letter: 'E',
  title: 'Excellence',
  description: 'We pursue the highest standards in clinical care and patient experience.',
  Icon: Award
}];
export function About() {
  return <div className="w-full bg-white" data-id="element-375">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy" data-id="element-376">
        <img src="https://cdn.magicpatterns.com/uploads/bPHihZ6KTfWZdQRzutubCy/maisha_edited_6.jpg" alt="Maisha Medical Center reception and waiting area" className="absolute inset-0 w-full h-full object-cover" data-id="element-377" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/70 to-brand-navy/30" data-id="element-378" />
        <div className="relative z-10 max-w-brand mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28" data-id="element-379">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl text-white" data-id="element-380">
            <span className="font-semibold tracking-wider text-sm uppercase mb-3 block text-[#FFFFFF]" data-id="element-381">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6 text-[#FFFFFF]" data-id="element-382">
              Trusted, Patient-Centered Care Since 2013
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed" data-id="element-383">
              Maisha Medical Center is committed to providing accessible,
              high-quality healthcare to individuals and families across
              Botswana.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 overflow-hidden" data-id="element-384">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-385">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-386">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="w-full lg:w-1/2 relative" data-id="element-387">
              <div className="grid grid-cols-2 gap-4" data-id="element-388">
                <img src="https://cdn.magicpatterns.com/uploads/n6zAT8tgvM5ciksZ9UXwaH/20231219005952_IMG_0691-01.jpg" alt="Maisha clinician performing an ear examination on a young patient" className="rounded-2xl shadow-soft object-cover h-72 w-full mt-8" data-id="element-389" />
                <img src="https://cdn.magicpatterns.com/uploads/abv6TkKQVb179pmoWXHRQg/maisha_edited.jpg" alt="Maisha physiotherapist assessing a patient in a wheelchair" className="rounded-2xl shadow-soft object-cover h-72 w-full" data-id="element-390" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="w-full lg:w-1/2" data-id="element-391">
              <span className="text-brand-red font-semibold tracking-wider text-sm uppercase mb-3 block" data-id="element-392">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-6 leading-tight" data-id="element-393">
                Dedicated to the Health of Our Community
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-394">
                NORA-PACK TECHNICS (PTY) LTD operates Maisha Medical Center,
                registered in Botswana since 2013.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed" data-id="element-395">
                Managed by{' '}
                <strong className="text-brand-navy font-semibold" data-id="element-396">
                  Dr. Gaston Nduenga Zanzambi
                </strong>
                , the center is committed to providing accessible, high-quality,
                and patient-centered healthcare for individuals and families
                across the region.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-brand-lighter/40" data-id="element-397">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-398">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-399">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-brand-red rounded-2xl p-8 md:p-10 text-white shadow-soft-lg" data-id="element-400">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-6" data-id="element-401">
                <Target className="w-6 h-6" data-id="element-402" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4" data-id="element-403">
                Our Mission
              </h3>
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-404">
                To promote health and well-being by delivering professional,
                compassionate, and evidence-based medical care.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="bg-brand-navy rounded-2xl p-8 md:p-10 text-white shadow-soft-lg" data-id="element-405">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-6" data-id="element-406">
                <Eye className="w-6 h-6 text-brand-gold" data-id="element-407" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4 text-[#FFFFFF]" data-id="element-408">
                Our Vision
              </h3>
              <p className="text-slate-200 text-lg leading-relaxed" data-id="element-409">
                To establish a first-class, patient-focused service that
                delivers excellence in a welcoming environment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values — I.C.A.R.E */}
      <section className="py-24" data-id="element-410">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-411">
          <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-412">
            <span className="text-brand-red font-semibold tracking-wider text-sm uppercase mb-3 block" data-id="element-413">
              What We Stand For
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-4" data-id="element-414">
              Our Values — I.C.A.R.E
            </h2>
            <p className="text-slate-600 text-lg" data-id="element-415">
              Five principles that guide everything we do at Maisha Medical
              Center.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-416">
            {VALUES.map(({
            letter,
            title,
            description,
            Icon
          }, idx) => <motion.div key={title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.08
          }} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-shadow text-center" data-id="element-417">
                <div className="w-14 h-14 rounded-2xl bg-brand-lighter flex items-center justify-center mx-auto mb-5" data-id="element-418">
                  <Icon className="w-7 h-7 text-brand-red" data-id="element-419" />
                </div>
                <div className="text-3xl font-heading font-bold text-brand-gold mb-1" data-id="element-420">
                  {letter}
                </div>
                <h3 className="font-heading font-bold text-lg text-brand-navy mb-2" data-id="element-421">
                  {title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-422">
                  {description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-id="element-423">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-424">
          <div className="relative overflow-hidden rounded-3xl bg-brand-navy px-8 py-14 md:px-16 md:py-16 text-center" data-id="element-425">
            <div className="relative z-10 max-w-2xl mx-auto" data-id="element-426">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4" data-id="element-427">
                Your health is our devotion
              </h2>
              <p className="text-slate-200 text-lg mb-8" data-id="element-428">
                Book an appointment today and experience patient-centered care
                at Maisha Medical Center.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-429">
                <Button href="/book" variant="primary" size="lg" icon={<Calendar className="w-5 h-5" data-id="element-431" />} data-id="element-430">
                  Book Appointment
                </Button>
                <a href="https://wa.me/26773241613" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] text-white text-lg font-medium px-8 py-4 hover:bg-[#20bd5a] transition-colors shadow-md hover:shadow-lg" data-id="element-432">
                  <MessageCircle className="w-5 h-5" data-id="element-433" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}